import { Category, getPosCategories } from "@/api/category-api";
import { getPosProducts } from "@/api/pos-api";
import { useCollectionData } from "@/hooks/use-api";
import { useMemo } from "react";
import { PosProductCard } from "./pos-product-card";
import type { TProductWithRelation } from "@/types/product-type";
import { LayoutGrid, Layers, Package } from "lucide-react";
import { usePosContext } from "../context/pos-context";

function filterBySearch(
  products: TProductWithRelation[],
  searchTerm: string
): TProductWithRelation[] {
  const trimmed = searchTerm.trim();
  if (!trimmed) return products;
  const terms = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  return products.filter((p) => {
    const searchable = [
      p.product.name,
      p.product.description,
      p.product.code,
      p.product.categoryName,
      ...p.productVariants.map((v) => v.name),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
}

function ProductCardSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden bg-white border border-gray-100 animate-pulse">
      <div className="aspect-[4/3] bg-gray-200" />
      <div className="p-3 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
  );
}

export function PosProductGrid() {
  const {
    activeCategoryId,
    setActiveCategoryId,
    searchTerm,
    displayMode,
    setDisplayMode,
  } = usePosContext();
  const { data: products, isLoading } = useCollectionData<TProductWithRelation[]>(
    "/pos/products",
    getPosProducts,
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
    }
  );
  const { data: categoriesData } = useCollectionData<Category[]>(
    "/pos/products/categories",
    getPosCategories,
    {
      refreshInterval: 60000,
      revalidateOnFocus: true,
    }
  );

  const productList = products ?? [];
  const allCategories = categoriesData ?? [];

  // Build dynamic storefront categories from categories that have storefront products
  // (plus fallback to product.categoryId/categoryName if categories endpoint hasn't loaded)
  const storefrontCategories = useMemo(() => {
    const catMap = new Map<
      string,
      {
        id: string;
        name: string;
        icon?: string | null;
        parentId?: string | null;
        sortOrder: number;
        count: number;
      }
    >();

    for (const c of allCategories) {
      catMap.set(String(c.id), {
        id: String(c.id),
        name: c.name,
        icon: c.icon,
        parentId: c.parentId ? String(c.parentId) : null,
        sortOrder: c.sortOrder ?? 0,
        count: 0,
      });
    }

    let uncategorizedCount = 0;
    for (const pwr of productList) {
      const cid = pwr.product.categoryId ? String(pwr.product.categoryId) : null;
      if (!cid) {
        uncategorizedCount++;
        continue;
      }
      const existing = catMap.get(cid);
      if (existing) {
        existing.count += 1;
      } else if (pwr.product.categoryName) {
        catMap.set(cid, {
          id: cid,
          name: pwr.product.categoryName,
          icon: null,
          parentId: null,
          sortOrder: 999,
          count: 1,
        });
      } else {
        uncategorizedCount++;
      }
    }

    // Also roll up child counts into parent category counts so parent tabs with subcategories show up
    const allEntries = Array.from(catMap.values());
    const activeTabs = allEntries
      .map((cat) => {
        const childrenCount = allEntries
          .filter((child) => child.parentId === cat.id)
          .reduce((sum, child) => sum + child.count, 0);
        return {
          ...cat,
          totalCount: cat.count + childrenCount,
        };
      })
      .filter((cat) => cat.totalCount > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

    return {
      tabs: activeTabs,
      uncategorizedCount,
    };
  }, [productList, allCategories]);

  // Progressive disclosure: only show category bar if at least 1 product on storefront is categorized
  const hasAnyCategorizedProducts = storefrontCategories.tabs.length > 0;

  const filteredProducts = useMemo(() => {
    let byCategory = productList;
    if (hasAnyCategorizedProducts && activeCategoryId !== "all") {
      if (activeCategoryId === "uncategorized") {
        byCategory = productList.filter((p) => !p.product.categoryId);
      } else {
        // Match either exact categoryId OR a child category whose parentId === activeCategoryId
        const childCategoryIds = new Set(
          storefrontCategories.tabs
            .filter((t) => t.parentId === activeCategoryId)
            .map((t) => t.id)
        );
        byCategory = productList.filter((p) => {
          const cid = p.product.categoryId ? String(p.product.categoryId) : null;
          return cid === activeCategoryId || (cid !== null && childCategoryIds.has(cid));
        });
      }
    }
    return filterBySearch(byCategory, searchTerm);
  }, [
    productList,
    hasAnyCategorizedProducts,
    activeCategoryId,
    storefrontCategories.tabs,
    searchTerm,
  ]);

  // Flatten variants for "variants" displayMode (e.g. Facebook Live style where each variant is a distinct item/photo)
  const flattenedVariantItems = useMemo(() => {
    const items: Array<{
      key: string;
      productWithRelation: TProductWithRelation;
      variant?: TProductWithRelation["productVariants"][number];
    }> = [];

    for (const pwr of filteredProducts) {
      if (pwr.productVariants.length === 0) {
        items.push({
          key: pwr.product.id,
          productWithRelation: pwr,
        });
      } else {
        for (const variant of pwr.productVariants) {
          items.push({
            key: `${pwr.product.id}-${variant.id}`,
            productWithRelation: pwr,
            variant,
          });
        }
      }
    }
    return items;
  }, [filteredProducts]);

  const hasMultiVariantProducts = useMemo(
    () => productList.some((p) => p.productVariants.length > 1),
    [productList]
  );

  const activeCategoryObj = storefrontCategories.tabs.find(
    (c) => c.id === activeCategoryId
  );
  const categoryLabel =
    activeCategoryId === "all"
      ? "All Menu"
      : activeCategoryId === "uncategorized"
        ? "Other"
        : activeCategoryObj
          ? `${activeCategoryObj.icon ? `${activeCategoryObj.icon} ` : ""}${activeCategoryObj.name}`
          : "All Menu";

  if (isLoading) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {categoryLabel}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Dynamic Horizontal Category Bar (SquarePOS / Shopify style) — auto-hidden when catalog is flat */}
      {hasAnyCategorizedProducts && (
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-3 no-scrollbar -mx-1 px-1">
          <button
            type="button"
            onClick={() => setActiveCategoryId("all")}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all border ${
              activeCategoryId === "all"
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-white text-slate-700 border-gray-200 hover:border-gray-300"
            }`}
          >
            <span>All</span>
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                activeCategoryId === "all"
                  ? "bg-white/20 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {productList.length}
            </span>
          </button>

          {storefrontCategories.tabs.map((cat) => {
            const isSelected = activeCategoryId === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategoryId(cat.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all border ${
                  isSelected
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-white text-slate-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                {cat.icon && <span>{cat.icon}</span>}
                <span>{cat.name}</span>
                <span
                  className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                    isSelected
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {cat.totalCount}
                </span>
              </button>
            );
          })}

          {storefrontCategories.uncategorizedCount > 0 && (
            <button
              type="button"
              onClick={() => setActiveCategoryId("uncategorized")}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all border ${
                activeCategoryId === "uncategorized"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-white text-slate-700 border-gray-200 hover:border-gray-300"
              }`}
            >
              <span>Other</span>
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  activeCategoryId === "uncategorized"
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {storefrontCategories.uncategorizedCount}
              </span>
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          {categoryLabel}
        </h2>

        {hasMultiVariantProducts && (
          <div className="inline-flex items-center rounded-lg bg-gray-100 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setDisplayMode("products")}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-all ${
                displayMode === "products"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Group variants inside each product card with quick option pills"
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              <span>Products ({filteredProducts.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode("variants")}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-all ${
                displayMode === "variants"
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Display every variant as its own individual card"
            >
              <Layers className="h-3.5 w-3.5" />
              <span>All Variants ({flattenedVariantItems.length})</span>
            </button>
          </div>
        )}
      </div>

      {displayMode === "variants" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5">
          {flattenedVariantItems.map((item) => (
            <PosProductCard
              key={item.key}
              productWithRelation={item.productWithRelation}
              forcedVariant={item.variant}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5">
          {filteredProducts.map((pwr) => (
            <PosProductCard key={pwr.product.id} productWithRelation={pwr} />
          ))}
        </div>
      )}

      {filteredProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-gray-100 p-6 mb-4">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
          <p className="text-muted-foreground font-medium">No products found</p>
          <p className="text-sm text-muted-foreground mt-1">
            {searchTerm
              ? "Try a different search term"
              : "No products in this category yet"}
          </p>
        </div>
      )}
    </div>
  );
}

